import { UtilErrorsInitConfig } from './util-errors.config';
import { getTransactionId } from './transaction';
import { computePerfAnnotations } from './transaction-perf-tags';
import { getSessionTags } from './session-tags';
import {
  FALLBACK_ROUTE_NAME,
  RouteContainerManager,
  RpcHandle,
} from './route-container';

type SentryModule = typeof import('./sentry-impl');

let _sentry: SentryModule | null = null;
let _initConfig: UtilErrorsInitConfig | null = null;
let _enabled = false;
let _containerManager: RouteContainerManager | null = null;

type NavigationRouter = ConstructorParameters<SentryModule['TraceService']>[0];
let _navigationRouter: NavigationRouter | null = null;
let _traceService: InstanceType<SentryModule['TraceService']> | null = null;

const _pendingErrors: unknown[] = [];
const _pendingTags: [string, string][] = [];

// Start loading Sentry immediately when this module is imported.
// The dynamic import creates a separate chunk that loads in parallel
// with Angular bootstrap instead of blocking it. Importing the narrow
// wrapper (instead of '@sentry/angular' directly) lets esbuild
// tree-shake unused integrations (Replay, Feedback, Canvas) out of
// the lazy chunk.
export const sentryReady: Promise<SentryModule> = import('./sentry-impl').then(
  (mod) => {
    _sentry = mod;

    if (_enabled && _initConfig) {
      performInit(mod, _initConfig);
      flushPending(mod);
    }

    return mod;
  }
);

function performInit(sentry: SentryModule, config: UtilErrorsInitConfig): void {
  sentry.init({
    release: config.release,
    dsn: config.sentryDsn,
    environment: config.environment,
    tunnel: config.tunnel,
    integrations: [
      sentry.browserTracingIntegration({
        traceFetch: false,
        traceXHR: false,
      }),
    ],
    tracesSampleRate: config.sentryTracingSampleRate,
    beforeSendTransaction(event) {
      const { tags, measurements } = computePerfAnnotations(event);
      if (Object.keys(tags).length > 0) {
        event.tags = { ...event.tags, ...tags };
      }
      if (Object.keys(measurements).length > 0) {
        event.measurements = { ...event.measurements, ...measurements };
      }
      return event;
    },
  });

  sentry.setTag('transaction_id', getTransactionId());

  for (const [name, value] of Object.entries(getSessionTags())) {
    sentry.setTag(name, value);
  }

  setupRouteContainers(sentry);
  setupNavigationTracing(sentry);
}

/**
 * Instantiate the SDK's TraceService once Sentry is initialised and a
 * router has been registered. It creates a `navigation` root span per
 * route change and renames pageload/navigation spans to the
 * parameterized route (e.g. `/study/:id`) on ResolveEnd — which is also
 * where route-visit containers get their names from. Instantiated here,
 * not via Angular DI, so @sentry/angular stays out of the initial bundle.
 */
function setupNavigationTracing(sentry: SentryModule): void {
  if (!_traceService && _navigationRouter) {
    _traceService = new sentry.TraceService(_navigationRouter);
  }
}

/**
 * Register the Angular Router used for navigation tracing. Called from
 * provideUtilErrors; safe to call before Sentry has loaded.
 */
export function registerNavigationRouter(router: NavigationRouter): void {
  _navigationRouter = router;
  if (_sentry && _containerManager) {
    setupNavigationTracing(_sentry);
  }
}

const ROUTE_CONTAINER_OP = 'route.activity';

/**
 * Wire up the RouteContainerManager (see route-container.ts): track the
 * live pageload/navigation idle root via client hooks, and end containers
 * when the page is hidden so the final one still ships before the tab
 * goes away.
 */
function setupRouteContainers(sentry: SentryModule): void {
  if (_containerManager) return;

  const manager = new RouteContainerManager({
    startContainer: (name) =>
      // A `null` active span detaches from any ambient (possibly leaked)
      // parent, so the container is a root span — billed as one
      // transaction covering all the RPC spans nested under it.
      sentry.withActiveSpan(null, () =>
        sentry.startInactiveSpan({
          name,
          op: ROUTE_CONTAINER_OP,
          forceTransaction: true,
          // Names come from TraceService-parameterized routes (e.g.
          // /study/:id), the same source navigation transactions use.
          attributes: { 'sentry.source': 'route' },
        })
      ),
    now: () => Date.now(),
  });
  _containerManager = manager;

  const client = sentry.getClient();
  client?.on('spanStart', (span) => {
    if (sentry.getRootSpan(span) !== span) return;
    const { op, description } = sentry.spanToJSON(span);
    if (op === 'pageload' || op === 'navigation') {
      manager.noteIdleRootStart(span, description ?? FALLBACK_ROUTE_NAME);
    }
  });
  client?.on('spanEnd', (span) =>
    // Pass the end-time name: TraceService renames pageload/navigation
    // roots to the parameterized route after they start.
    manager.noteIdleRootEnd(span, sentry.spanToJSON(span).description)
  );

  // The pageload idle span starts inside sentry.init(), before the hooks
  // above are attached — seed it if it's still recording.
  const active = sentry.getActiveSpan();
  if (active) {
    const root = sentry.getRootSpan(active);
    const { op, description } = sentry.spanToJSON(root);
    if (
      (op === 'pageload' || op === 'navigation') &&
      (root as RecordingSpan).isRecording?.() === true
    ) {
      manager.noteIdleRootStart(root, description ?? FALLBACK_ROUTE_NAME);
    }
  }

  if (typeof window !== 'undefined') {
    // The tab is actually going away: end everything now, dropping
    // still-in-flight child spans — better than losing the container.
    window.addEventListener('pagehide', () => manager.pageHidden());
    // The tab is merely hidden (tab switch, app switch): close the
    // container gracefully so in-flight RPCs — which keep running in
    // hidden tabs — still land in it before it ships.
    window.document.addEventListener('visibilitychange', () => {
      if (window.document.visibilityState === 'hidden') {
        manager.closeCurrent();
      }
    });
  }
}

/**
 * Notify the container manager that an Angular router navigation started,
 * closing the current route's container. Wired up in provideUtilErrors.
 */
export function notifyNavigationStart(): void {
  _containerManager?.navigationStarted();
}

function flushPending(sentry: SentryModule): void {
  for (const [name, value] of _pendingTags) {
    sentry.setTag(name, value);
  }
  _pendingTags.length = 0;

  for (const error of _pendingErrors) {
    sentry.captureException(error);
  }
  _pendingErrors.length = 0;
}

export function initSentry(config: UtilErrorsInitConfig): void {
  _initConfig = config;

  if (_enabled && _sentry) {
    performInit(_sentry, config);
    flushPending(_sentry);
  }
}

/**
 * Activate Sentry reporting. Called from the environment initializer
 * after verifying that debug mode is not active.
 */
export function enableSentry(): void {
  _enabled = true;
  if (_sentry && _initConfig) {
    performInit(_sentry, _initConfig);
    flushPending(_sentry);
  }
}

interface RecordingSpan {
  isRecording?: () => boolean;
}

export function captureException(error: unknown): void {
  if (_sentry) {
    _sentry.captureException(error);
  } else {
    _pendingErrors.push(error);
  }
}

export function setTag(name: string, value: string): void {
  if (_sentry) {
    _sentry.setTag(name, value);
  } else {
    _pendingTags.push([name, value]);
  }
}

export function createErrorHandler(
  ...args: Parameters<SentryModule['createErrorHandler']>
): ReturnType<SentryModule['createErrorHandler']> {
  if (_sentry) {
    return _sentry.createErrorHandler(...args);
  }

  // Return a proxy handler that queues errors until Sentry loads.
  // Once loaded, future calls go directly to the real handler.
  let realHandler: ReturnType<SentryModule['createErrorHandler']> | null = null;
  sentryReady.then((sentry) => {
    realHandler = sentry.createErrorHandler(...args);
  });

  return {
    handleError(error: unknown) {
      if (realHandler) {
        realHandler.handleError(error);
      } else {
        _pendingErrors.push(error);
      }
    },
  } as ReturnType<SentryModule['createErrorHandler']>;
}

export async function startSpan<T>(
  options: Parameters<SentryModule['startSpan']>[0],
  callback: Parameters<SentryModule['startSpan']>[1]
): Promise<T> {
  if (_sentry) {
    return _sentry.startSpan(options, callback) as T;
  }
  // No tracing available yet — run the callback with a no-op span
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const noopSpan = { setAttribute: () => {} };
  return callback(noopSpan as never) as T;
}

type WithActiveSpanParent = Parameters<SentryModule['withActiveSpan']>[0];

/**
 * Trace an RPC as a span with an *explicitly* chosen parent — the live
 * pageload/navigation root, or the current route-visit container span
 * (see route-container.ts). Never inherits the ambient active span: the
 * browser SDK has no async-context tracking, so the ambient span leaks
 * across awaits and a span parented to it can end up in a transaction
 * that has already shipped, silently dropping the span's data.
 */
export async function startRpcSpan<T>(
  options: Parameters<SentryModule['startSpan']>[0],
  callback: Parameters<SentryModule['startSpan']>[1]
): Promise<T> {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const noopSpan = { setAttribute: () => {} };
  const sentry = _sentry;
  const manager = _containerManager;
  if (!sentry || !manager) {
    return callback(noopSpan as never) as T;
  }

  // Tracing must never fail the RPC it observes. If any Sentry SDK call
  // throws (client torn down, integration fault, container creation), fall
  // back to running the RPC untraced — but still propagate the callback's
  // own error, which is the real RPC failure. `callbackStarted` tells the
  // two apart: once the callback has run, any throw is the RPC's.
  let rpc: RpcHandle | undefined;
  let callbackStarted = false;
  const tracedCallback = ((span: never) => {
    callbackStarted = true;
    return callback(span);
  }) as typeof callback;

  try {
    rpc = manager.beginRpc();
    return (await sentry.withActiveSpan(
      rpc.parent as WithActiveSpanParent,
      () => sentry.startSpan(options, tracedCallback)
    )) as T;
  } catch (err) {
    if (callbackStarted) throw err;
    return callback(noopSpan as never) as T;
  } finally {
    rpc?.done();
  }
}
